export const META_PIXEL_ID = '2314886982646161'

const PIXEL_SRC = 'https://connect.facebook.net/en_US/fbevents.js'

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

let fallbackTimer: number | undefined

/**
 * Meta Pixel base code.
 *
 * Se implementa en JS del bundle (no como <script> inline) porque las páginas
 * publicadas aplican una CSP que bloquea scripts inline: ese bloqueo es la causa
 * típica del "No pixel found" del Pixel Helper. El comportamiento es idéntico al
 * snippet oficial: define fbq, encola eventos, carga fbevents.js y ejecuta
 * fbq('init', '2314886982646161') + fbq('track', 'PageView').
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (window.fbq) return

  const n: FbqFn = function (...args: unknown[]) {
    if (n.callMethod) {
      n.callMethod.apply(n, args)
    } else {
      n.queue?.push(args)
    }
  } as FbqFn

  window.fbq = n
  if (!window._fbq) window._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  const script = document.createElement('script')
  script.async = true
  script.src = PIXEL_SRC
  script.onerror = () => sendBeacon('PageView')
  const firstScript = document.getElementsByTagName('script')[0]
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }

  n('init', META_PIXEL_ID)
  n('track', 'PageView')

  // Si fbevents.js nunca llega (CSP, bloqueador, red), al menos registramos la vista.
  fallbackTimer = window.setTimeout(() => {
    if (!isPixelLoaded()) sendBeacon('PageView')
  }, 4000)
}

/** Beacon estilo <noscript>: registra el evento sin depender de fbevents.js. */
function sendBeacon(eventName: string, params: Record<string, string> = {}): void {
  if (typeof window === 'undefined') return
  const query = new URLSearchParams({
    id: META_PIXEL_ID,
    ev: eventName,
    noscript: '1',
    ...params,
  })
  const img = new Image(1, 1)
  img.src = `https://www.facebook.com/tr?${query.toString()}`
}

/** PageView adicional para las navegaciones internas del funnel (SPA). */
export function trackPageView(): void {
  initMetaPixel()
  if (isPixelLoaded() || window.fbq) {
    window.fbq?.('track', 'PageView')
  } else {
    sendBeacon('PageView')
  }
}

export function trackAddToCart(payload: {
  content_name: string
  content_ids: string[]
  value: number
  currency?: string
}): void {
  initMetaPixel()
  window.fbq?.('track', 'AddToCart', {
    content_type: 'product',
    currency: 'USD',
    ...payload,
  })
  if (!isPixelLoaded()) {
    sendBeacon('AddToCart', {
      'cd[value]': String(payload.value),
      'cd[currency]': payload.currency ?? 'USD',
      'cd[content_name]': payload.content_name,
    })
  }
}

/** True cuando fbevents.js ya cargó y los eventos se envían de verdad. */
export function isPixelLoaded(): boolean {
  return typeof window !== 'undefined' && Boolean(window.fbq?.callMethod)
}

/**
 * Diagnóstico rápido desde la consola del navegador: window.__pixelStatus()
 * Devuelve si el base code corrió, si fbevents.js cargó y el ID configurado.
 */
export function exposePixelDiagnostics(): void {
  if (typeof window === 'undefined') return
  ;(window as unknown as Record<string, unknown>).__pixelStatus = () => ({
    pixelId: META_PIXEL_ID,
    fbqDefined: Boolean(window.fbq),
    fbeventsLoaded: isPixelLoaded(),
    queued: window.fbq?.queue?.length ?? 0,
    scriptPresent: Boolean(document.querySelector(`script[src="${PIXEL_SRC}"]`)),
  })
}

/** Limpia el temporizador del fallback (útil en hot reload). */
export function clearPixelFallback(): void {
  if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer)
}
